import ActionsButton                         from '@/components/actionsButton/ActionsButton';
import ApiCollection                         from '@widesk-core/models/ApiCollection';
import { Box }                               from '@chakra-ui/react';
import DeliverableTransferModel              from '@/models/services/shop/DeliverableTransferModel';
import { HiCheck }                           from 'react-icons/hi';
import { Tag }                               from '@/components/ui/tag';
import { Text }                              from '@chakra-ui/react';
import authStore                             from '@/stores/authStore';
import { observer }                          from 'mobx-react';
import { t }                                 from 'i18next';
import useScreenSize                         from '@/hooks/useScreenSize';
import { useTranslation }                    from 'react-i18next';
import { IoMdInformationCircleOutline }      from 'react-icons/io';
import { ModalData }                         from '@/components/modal/Modal';
import { toaster }                           from '@/components/ui/toaster';
import DeliverableTransferStatusHistoryModel from '@/models/services/shop/DeliverableTransferStatusHistoryModel';
import DeliverableTransferStatusModel        from '@/models/services/shop/DeliverableTransferStatusModel';
import PagedCollection                       from '@widesk-core/models/PagedCollection';
import dayjs                                 from 'dayjs';
import Image                                 from '@/components/Image/Image';
import React                                 from 'react';
import { SupportedLanguage }                 from '@/hooks/useTranslations.tsx';

interface ITransferListRowProps {
	deliverableTransfer: DeliverableTransferModel;
	deliverableTransferCollection: PagedCollection<DeliverableTransferModel>;
	deliverableTransferStatusCollection: ApiCollection<DeliverableTransferStatusModel>;
	setModalData: React.Dispatch<React.SetStateAction<ModalData | undefined>>;
	setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const TransferListRow = observer((props: ITransferListRowProps) => {
	const {
		deliverableTransfer,
		deliverableTransferCollection,
		deliverableTransferStatusCollection,
		setModalData,
		setModalIsOpen,
	} = props;
	const { i18n } = useTranslation();
	const { isLG } = useScreenSize();

	const transferIsWaiting = deliverableTransfer.deliverableTransferStatus.reference === 'waiting';
	const transferIsAccepeted = deliverableTransfer.deliverableTransferStatus.reference === 'accepted';

	const acceptStatus = deliverableTransferStatusCollection.find(m => m.reference === 'accepted') as DeliverableTransferStatusModel;
	const cancelStatus = deliverableTransferStatusCollection.find(m => m.reference === 'canceled') as DeliverableTransferStatusModel;
	const refuseStatus = deliverableTransferStatusCollection.find(m => m.reference === 'refused') as DeliverableTransferStatusModel;

	const initiatedByYourself = (
		deliverableTransfer.currentOwner.id
		&& (deliverableTransfer.currentOwner.attributes.userUrn === authStore.currentUser.urn)
	) || false;

	const updateTransferHistory = async (deliverableTransfer: DeliverableTransferModel, status: DeliverableTransferStatusModel) => {
		const isAccepting = status.reference === 'accepted';
		const isCancelling = status.reference === 'canceled';
		const isRefusing = status.reference === 'refused';

		const titleComponent =
			<Text>{t(`pages.transfers.modals.${isAccepting ? 'acceptTransfer' : isCancelling ? 'cancelTransfer' : isRefusing ? 'refuseTransfer' : ''}.title`)}</Text>;
		const bodyComponent =
			<Text>{t(`pages.transfers.modals.${isAccepting ? 'acceptTransfer' : isCancelling ? 'cancelTransfer' : isRefusing ? 'refuseTransfer' : ''}.message`)}</Text>;

		setModalData({
			isDangerous: isAccepting ? false : true,
			setIsOpen: setModalIsOpen,
			titleComponent: titleComponent,
			bodyComponent: bodyComponent,
			onClose: () => {
				setModalIsOpen(false);
				setModalData(undefined);
			},
			onConfirm: async () => {
				const history = new DeliverableTransferStatusHistoryModel()

				await history.patch({
					deliverableTransfer: deliverableTransfer.urn,
					deliverableTransferStatus: status.urn,
				});
			},
			onError: () => {
				toaster.create({
					title: t(`pages.transfers.modals.${isAccepting ? 'acceptTransfer' : isCancelling ? 'cancelTransfer' : isRefusing ? 'refuseTransfer' : ''}.error`),
					type: 'error',
				});
			},
			onSuccess: async () => {
				toaster.create({
					title: t(`pages.transfers.modals.${isAccepting ? 'acceptTransfer' : isCancelling ? 'cancelTransfer' : isRefusing ? 'refuseTransfer' : ''}.success`),
					type: 'success',
				});
				await deliverableTransferCollection.list();
				await deliverableTransferCollection.whenIsLoaded(m => m.deliverableTransferStatus);
				await deliverableTransferCollection.whenIsLoaded(m => m.productVariant.product.entertainment);
				setModalIsOpen(false);
				setModalData(undefined);
			},
		})
		setModalIsOpen(true);
	};

	return (
		<Box
			display="flex"
			alignItems="center"
			justifyContent="space-between"
		>
			<Box
				display="flex"
				alignItems="center"
				gap="25px"
			>
				<Box width="65px" height="65px">
					<Image
						errorSize="xl"
						src={deliverableTransfer.productVariant.getId('product') ? deliverableTransfer.productVariant.product.image : ''}
						type="profile"
						height="65px"
						width="65px"
						borderRadius="8px"
					/>
				</Box>
				<Box
					display="flex"
					flexDirection="column"
					gap="5px"
				>
					<Box>
						<Text
							className="poppins-semibold"
							fontSize="16px"
							textDecoration="underline"
						>
							{deliverableTransfer.productVariant.getFullTitle(i18n?.language as SupportedLanguage)}
						</Text>
					</Box>

					<Box
						display="flex"
						flexDirection="column"
						gap="2px"
					>
						<Box fontSize="12px">
							<Text>{deliverableTransfer.productVariant.product?.entertainment?.label}</Text>
						</Box>
						{
							!isLG
							&& initiatedByYourself
							&& <Text fontStyle="italic"
							         opacity={0.8}>{t('pages.transfers.initiatedByYourself', {
								day: dayjs(deliverableTransfer.updatedAt).format('D'),
								month: dayjs(deliverableTransfer.updatedAt).format('MMMM'),
								year: dayjs(deliverableTransfer.updatedAt).format('YYYY'),
							})}</Text>
						}
					</Box>
					{
						!isLG
						&& (
							<Box>
								<Tag
									colorPalette={deliverableTransfer.deliverableTransferStatus.color}
									startElement={transferIsWaiting ?
										<IoMdInformationCircleOutline /> : transferIsAccepeted ? <HiCheck /> : undefined}
									size="md"
								>
									{deliverableTransfer.deliverableTransferStatus.label}
								</Tag>
							</Box>
						)
					}
				</Box>
			</Box>
			<Box
				display="flex"
				alignItems="center"
				gap="20px"
			>
				{
					isLG
					&& <Tag
						colorPalette={deliverableTransfer.deliverableTransferStatus.color}
						startElement={transferIsWaiting ? <IoMdInformationCircleOutline /> : transferIsAccepeted ?
							<HiCheck /> : undefined}
						size="md"
					>
						{deliverableTransfer.deliverableTransferStatus.label}
					</Tag>
				}
				{
					isLG
					&& initiatedByYourself
					&& <Text fontStyle="italic"
					         opacity={0.8}>{t('pages.transfers.initiatedByYourself', {
						day: dayjs(deliverableTransfer.updatedAt).format('D'),
						month: dayjs(deliverableTransfer.updatedAt).format('MMMM'),
						year: dayjs(deliverableTransfer.updatedAt).format('YYYY'),
					})}</Text>
				}
				<ActionsButton
					actions={
						[
							{
								label: t('pages.transfers.actions.accept.label'),
								do: () => updateTransferHistory(deliverableTransfer, acceptStatus),
								disabled: initiatedByYourself || !transferIsWaiting,
							},
							{
								type: 'remove',
								label: initiatedByYourself ? t('pages.transfers.actions.cancel.label') : t('pages.transfers.actions.refuse.label'),
								do: () => initiatedByYourself ? updateTransferHistory(deliverableTransfer, cancelStatus) : updateTransferHistory(deliverableTransfer, refuseStatus),
								disabled: !transferIsWaiting,
							},
						]
					}
				/>
			</Box>
		</Box>
	)
})

export default TransferListRow;